<template>
  <div
    v-if="products.length || pending"
    ref="showcaseRef"
    class="-mx-4 px-4 overflow-x-hidden md:mx-0 md:px-0 md:overflow-x-visible my-8"
    :data-test-id="dataTestId"
  >
    <Heading
      :level="2"
      :title="title"
      h-class="text-h5"
      class="mb-3 md:mb-2 kft-heading--left"
      :use-h-tag="true"
    />

    <ProductsLoader
      v-show="pending"
      is-horizontal
      :products-count="loaderProductsCount"
    />

    <div
      v-show="!pending && products.length"
      class="event-horizontal-showcase__scroll-area relative"
    >
      <button
        v-show="canScrollLeft"
        type="button"
        class="event-horizontal-showcase__arrow event-horizontal-showcase__arrow--left hidden md:flex"
        :aria-label="t('general.pagination.previous')"
        @click="scrollByDirection('left')"
      >
        <span
          class="event-horizontal-showcase__arrow-button flex items-center justify-center"
        >
          <ChevronLeft class="mr-0.5 text-black" />
        </span>
      </button>

      <button
        v-show="canScrollRight"
        type="button"
        class="event-horizontal-showcase__arrow event-horizontal-showcase__arrow--right hidden md:flex"
        :aria-label="t('general.pagination.next')"
        @click="scrollByDirection('right')"
      >
        <span
          class="event-horizontal-showcase__arrow-button flex items-center justify-center"
        >
          <ChevronRight class="ml-0.5 text-black" />
        </span>
      </button>

      <div
        ref="scrollContainerRef"
        v-dragscroll.x="enableDragScroll"
        class="event-horizontal-showcase__cards flex flex-nowrap gap-4 py-3"
        @scroll.passive="updateScrollState"
        @dragscrollstart="onDragStart"
        @click.capture="onDragClick"
      >
        <ProductCard
          v-for="(item, index) in products"
          :key="item.objectID"
          class="event-horizontal-showcase__card shrink-0"
          v-bind="getProductCardProps(item, index)"
          is-slide
          :before-navigation-callback="() => handleClickOnCard(item, index)"
          @click:wishlist="handleClickWishlist(item)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { dragscroll } from 'vue-dragscroll'
import ChevronRight from '~/assets/icons/chevron-right.svg?component'
import ChevronLeft from '~/assets/icons/chevron-left.svg?component'
import type { AlgoliaEventItem } from '~/composables/types/algolia'
import type { ProductCardImageBadge } from '~/components/product/ProductCardImage.vue'
import type { GtmItemListId } from '~/composables/useKftGtm/types'
import {
  sendViewItemList,
  sendSelectListItem,
} from '~/composables/useGtmEvents'
import { useAlgoliaConversion } from '~/composables/algolia/useAlgoliaConversion'
import { useSendEventToAlgolia } from '~/composables/product/useProduct'
import { useGetFormattedDuration } from '~/composables/useGetFormattedDuration'
import { useWishlistSidebarUiState } from '~/composables/useUiState'
import { useWishlist } from '~/composables/useWishlist'
import {
  useIntersectionObserver,
  IMPRESSION_OBSERVER_OPTIONS,
} from '~/composables/useIntersectionObserver'
import { algoliaEventGetters } from '~/utils/algoliaEventGetters'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'

const vDragscroll = dragscroll

const DESKTOP_VIEWPORT = '(min-width: 768px)'

type ShowcaseProduct = Partial<AlgoliaEventItem> &
  Pick<AlgoliaEventItem, 'objectID'>

const {
  products,
  pending = false,
  title,
  gtmItemListId,
  algoliaInsightsLabel,
  dataTestId = undefined,
  loaderProductsCount = 6,
} = defineProps<{
  products: ShowcaseProduct[]
  pending?: boolean
  title: string
  gtmItemListId: GtmItemListId
  algoliaInsightsLabel: string
  dataTestId?: string
  loaderProductsCount?: number
}>()

const { t, locale } = useI18n()
const { isMobile } = useDevice()
const { client: insightsClient, token: userToken } = useAlgoliaSearchInsights()
const { sendViewedObjectIDs } = useAlgoliaConversion()
const {
  addAlgoliaEventItem,
  isAlgoliaEventItemOnWishlist,
  removeAlgoliaEventItem,
} = useWishlist()
const { toggle: toggleWishlistSidebar } = useWishlistSidebarUiState()

const localeBCP47 = convertLocaleIntoBCP47(locale.value)
const fnGetFormattedDuration = (duration: unknown) =>
  useGetFormattedDuration(duration)

const scrollContainerRef = ref<HTMLElement | null>(null)
const enableDragScroll = ref(false)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)
const dragged = ref(false)
const dragTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

const updateScrollState = () => {
  const container = scrollContainerRef.value
  if (!container) return

  canScrollLeft.value = container.scrollLeft > 50
  canScrollRight.value =
    container.scrollLeft + container.offsetWidth < container.scrollWidth - 50
}

const scrollByDirection = (direction: 'left' | 'right') => {
  const container = scrollContainerRef.value
  if (!container) return

  const offset =
    direction === 'right' ? container.offsetWidth : -container.offsetWidth

  container.scrollTo({
    left: container.scrollLeft + offset,
    behavior: 'smooth',
  })
}

const onDragStart = () => {
  if (!enableDragScroll.value) return

  if (dragTimeout.value) {
    clearTimeout(dragTimeout.value)
  }

  dragged.value = false
  dragTimeout.value = setTimeout(() => {
    dragged.value = true
  }, 100)
}

const onDragClick = (event: Event) => {
  if (!enableDragScroll.value || !dragged.value) return

  event.preventDefault()
  dragged.value = false
}

const getBadges = (item: ShowcaseProduct): ProductCardImageBadge[] => {
  const badges: ProductCardImageBadge[] = []
  const generalBadgeLabel = algoliaEventGetters.getBadgeLabel(
    item as AlgoliaEventItem,
    t
  )

  if (generalBadgeLabel) {
    badges.push({
      label: generalBadgeLabel,
      variant: 'default',
    })
  }

  if (algoliaEventGetters.getHasPromotions(item as AlgoliaEventItem)) {
    badges.push({ label: t('product.card.sale'), variant: 'promo' })
  }

  return badges
}

const getProductCardProps = (item: ShowcaseProduct, index: number) => ({
  dataInsightsObjectId: item.objectID,
  duration: fnGetFormattedDuration(
    algoliaEventGetters.fnGetDuration(item as AlgoliaEventItem)
  ),
  durationTitle: t('product.card.duration'),
  minNumberOfPeople: item.min_number_of_people ?? 0,
  maxNumberOfPeople: item.max_number_of_people ?? 0,
  image: algoliaEventGetters.getPrincipalImage(item as AlgoliaEventItem),
  imageLoading: 'lazy' as const,
  link: algoliaEventGetters.fnGetLink(item as AlgoliaEventItem),
  productCategory: algoliaEventGetters.fnGetMainCategory(
    item as AlgoliaEventItem
  ),
  productType: '',
  badges: getBadges(item),
  rating: algoliaEventGetters.getAverageRating(
    item as AlgoliaEventItem,
    localeBCP47
  ),
  isPartnerRating: algoliaEventGetters.getIsPartnerRating(
    item as AlgoliaEventItem
  ),
  isThirdPartyRating: algoliaEventGetters.getIsThirdPartyRating(
    item as AlgoliaEventItem
  ),
  regularPrice: algoliaEventGetters.getRegularPriceAmount(
    item as AlgoliaEventItem
  ),
  specialPrice: algoliaEventGetters.getSpecialPriceAmount(
    item as AlgoliaEventItem
  ),
  priceSuffix: algoliaEventGetters.getIsGroupPricing(item as AlgoliaEventItem)
    ? t('product.card.price.groupPricingSuffix', {
        count: item.min_number_of_people,
      })
    : undefined,
  showAddToCartButton: false,
  style: { '--index': index },
  title: item.title || 'event-horizontal-showcase',
  alt: algoliaEventGetters.getPrincipalAlt(item as AlgoliaEventItem),
  wishlistIcon: true,
  isOnWishlist: isAlgoliaEventItemOnWishlist(item as AlgoliaEventItem),
  hasShippingCosts: algoliaEventGetters.hasShippingCosts(
    item as AlgoliaEventItem
  ),
  isTeamevent: algoliaEventGetters.getIsTeamevent(item as AlgoliaEventItem),
  isBoxEvent: algoliaEventGetters.isBoxEvent(item as AlgoliaEventItem),
  openInNewTab: !isMobile,
  listProductObjectId: item.objectID,
})

const handleClickWishlist = (item: ShowcaseProduct) => {
  const algoliaItem = item as AlgoliaEventItem

  if (isAlgoliaEventItemOnWishlist(algoliaItem)) {
    removeAlgoliaEventItem(algoliaItem)
  } else {
    addAlgoliaEventItem(algoliaItem)
    toggleWishlistSidebar()
  }
}

const handleClickOnCard = (item: ShowcaseProduct, index: number) => {
  const algoliaItem = item as AlgoliaEventItem

  useSendEventToAlgolia({
    objectID: item.objectID,
    position: index + 1,
    algoliaInsightsClient: insightsClient,
    label: algoliaInsightsLabel,
    queryId: '',
    locale: locale.value,
    userToken: userToken.value || '',
  })

  sendSelectListItem(gtmItemListId, [algoliaItem], index)
}

const showcaseRef = ref<HTMLElement | null>(null)
const { intersectionHappened } = useIntersectionObserver(
  showcaseRef,
  IMPRESSION_OBSERVER_OPTIONS
)
const hasTrackedView = ref(false)

watch(
  [() => products, intersectionHappened],
  ([items, hasIntersected]) => {
    if (
      !items.length ||
      !hasIntersected ||
      hasTrackedView.value ||
      !import.meta.client
    ) {
      return
    }

    sendViewItemList(gtmItemListId, items as AlgoliaEventItem[])
    sendViewedObjectIDs(items as AlgoliaEventItem[], algoliaInsightsLabel)
    hasTrackedView.value = true
  },
  { immediate: false }
)

watch(
  () => products,
  () => {
    nextTick(() => {
      updateScrollState()
    })
  },
  { flush: 'post' }
)

onMounted(() => {
  if (!import.meta.client) return

  const mediaQuery = window.matchMedia(DESKTOP_VIEWPORT)
  const syncDragScroll = () => {
    enableDragScroll.value = mediaQuery.matches
    nextTick(() => {
      updateScrollState()
    })
  }

  syncDragScroll()
  mediaQuery.addEventListener('change', syncDragScroll)

  onBeforeUnmount(() => {
    mediaQuery.removeEventListener('change', syncDragScroll)
  })
})
</script>

<style lang="postcss" scoped>
.event-horizontal-showcase__cards {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  touch-action: pan-x pan-y;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.event-horizontal-showcase__cards::-webkit-scrollbar {
  display: none;
}

.event-horizontal-showcase__card {
  width: 244px;
  scroll-snap-align: start;
}

.event-horizontal-showcase__card :deep(.product-card) {
  @apply flex-col;
}

.event-horizontal-showcase__card :deep(.product-card__content) {
  @apply w-full px-1 pt-1.5 pl-0;
}

.event-horizontal-showcase__card :deep(.card-image-wrapper) {
  @apply w-full h-[180px];
}

@media (min-width: 768px) {
  .event-horizontal-showcase__cards {
    overflow-x: hidden;
    scroll-snap-type: none;
    touch-action: auto;
  }

  .event-horizontal-showcase__card {
    width: 296px;
  }

  .event-horizontal-showcase__card :deep(.card-image-wrapper) {
    @apply h-[222px];
  }
}

.event-horizontal-showcase__arrow {
  position: absolute;
  top: 0;
  z-index: 3;
  height: 69%;
  width: 50px;
  align-items: center;
  border: 0;
  padding: 0;
  cursor: pointer;
}

.event-horizontal-showcase__arrow--left {
  left: 0;
  justify-content: flex-start;
  background: linear-gradient(to right, var(--color-white), rgba(0, 0, 0, 0));
}

.event-horizontal-showcase__arrow--right {
  right: 0;
  justify-content: flex-end;
  background: linear-gradient(to right, rgba(0, 0, 0, 0), var(--color-white));
}

.event-horizontal-showcase__arrow-button {
  @apply w-10 h-10 rounded-full border border-gray-200 bg-white;
}
</style>
